import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useOrulAuth } from '@rhaimes/react'
import { AxiosError } from 'axios'


import { Spinner } from 'core/components'
import { wait } from 'core/helpers'
import routes from 'core/routes'
import { api } from 'core/services'
import { buscarOrgs } from 'core/services/orgs-fetch'

import { AuthModule } from 'modules/auth'
import { ConsoleModule } from 'modules/console'

import { AuthContext } from 'providers/auth'
import { ThemeContext } from 'providers/theme'

import { TRootReducer } from 'store'
import { setAppCurrentOrg, setAppData } from 'store/reducers/app/actions'
import { TAppCurrentOrg, TAppOrgsState } from 'store/reducers/app/types'

import { Wrapper } from './styles'

function App() {
  const { key } = useParams()
  const { app } = useSelector((state: TRootReducer) => state)
  const { isAuthenticated, signOut } = useContext(AuthContext)
  const { setColors, theme } = useContext(ThemeContext)
  const { refreshSession } = useOrulAuth()
  /** Uma renovação por sessão: lista vazia legítima não pode virar laço de refresh. */
  const jaRenovouPorListaVazia = useRef(false)
  const [keyOrg, setOrgKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const fetchOrgs = useCallback(async () => {
    setLoading(true)

    try {
      const { orgs, renovou } = await buscarOrgs<TAppOrgsState>({
        refreshSession,
        permitirRenovacao: !jaRenovouPorListaVazia.current,
      })

      if (renovou) {
        jaRenovouPorListaVazia.current = true
      }

      dispatch(
        setAppData({
          key: null,
          orgs,
          currentOrg: null,
          fetched: true,
        }),
      )
    } catch (error) {
      const { response } = error as AxiosError<any>

      if (response && response.status === 401) {
        signOut()
      }
    }

    setLoading(false)
  }, [dispatch, refreshSession, signOut])

  const fetchOrgSettings = useCallback(
    async (orgKey: string) => {
      setLoading(true)

      try {
        const { data } = await api.get<TAppCurrentOrg>(`orgs/${orgKey}`)

        dispatch(setAppCurrentOrg(data))

        setColors({ ...data.settings.colors } as any)
      } catch (error) {
        const { response } = error as AxiosError<any>
        if (response && response.status === 404) {
          signOut()
        }
      }

      setLoading(false)
    },
    [dispatch, setColors, signOut],
  )

  const userAuthAcknowledge = useCallback(async () => {
    try {
      await api.get(routes.api.user.acknowledge)
    } catch {}
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      wait(500).then(userAuthAcknowledge)
    }
  }, [isAuthenticated, userAuthAcknowledge])

  useEffect(() => {
    if (keyOrg && app.key !== keyOrg) {
      fetchOrgSettings(keyOrg)
    }
  }, [app.key, fetchOrgSettings, keyOrg])

  useEffect(() => {
    if (!app.fetched && isAuthenticated) {
      fetchOrgs()
    }
  }, [app.fetched, fetchOrgs, isAuthenticated])

  useEffect(() => {
    if (key && !loading && app.fetched) {
      setOrgKey(key)
    }
  }, [key, loading, app.fetched])

  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute(
        'content',
        isAuthenticated ? theme.colors.primary : '#ffffff',
      )
  }, [theme.colors, isAuthenticated])

  return (
    <Wrapper>
      {(loading && !isAuthenticated) || (keyOrg && !app.currentOrg) ? (
        <Spinner />
      ) : isAuthenticated ? (
        <ConsoleModule />
      ) : (
        <AuthModule />
      )}
    </Wrapper>
  )
}

const phone = new URLSearchParams(window.location.search).get('phone')

if (phone && phone.length === 13 && !Number.isNaN(Number(phone))) {
  localStorage.setItem(
    'phone_number',
    phone
      .slice(2)
      .replace(/\D/g, '')
      .replace(/(^\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4,5})(\d{4}$)/, '$1-$2'),
  )
}

export default App
